import { useEffect, useMemo, useState } from "react";
import { useCookies } from "react-cookie";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useGetCallingListQuery, useGetListInstanceQuery } from "@/store/RTKQuery/clsSlice";
import { getArchiveOfDialogues, selectArchiveList } from "@/store/ses/sesArchiveSlice";
import { ICurrnetDestiantionTableSelection, ColumnMeta } from "./CallingLineList.props";
import { getNowTime, getOneMonthAgo } from "./CallingLineList.const";

import { IGetArchiveOfDialoguesProps } from "@/types/ses/sesArchiveTypes";
import { ICall } from "@/types/clsTypes";
import { useAccessRight, useTranslate } from "@/hooks";
import { selectClsHints } from "@/store/hintsSlice";
import { selectRobotList } from "@/store/ses/sesRobotSlice";

export const useCallingList = (/* tableMode: "observerMode" | "settingsMode", */ currentCall: ICall | undefined, onRowClick: (call: any) => void) => {
	const [cookies] = useCookies(["cls"]);
	const displayMode = useMemo(() => (cookies?.cls?.displayMode ? JSON.parse(cookies.cls.displayMode) : null), [cookies]);

	const [selectedCallListId, setSelectedCallListId] = useState<string | null>(null);
	const [currentColumns, setCurrentColumns] = useState<ColumnMeta[]>([]);
	const [selectedRowId, setSelectedRowId] = useState<string>("none");
	const [selectedRowData, setSelectedRowData] = useState<ICurrnetDestiantionTableSelection>();
	const [selectedCallRowId, setSelectedCallRowId] = useState<string>("none");
	const [selectedCallsList, setSelectedCallsList] = useState<{ calls: ICall[]; metadata: Record<string, string> }>();
	const [callsIdsBySearch, setCallsIdsBySearch] = useState<string[]>([]);
	const [isFiltersActive, setIsFiltersActive] = useState(false);

	const initialStartTime = getOneMonthAgo();
	const initialEndTime = getNowTime();
	const initialSearch = "";

	const [startTime, setStartTime] = useState("");
	const [endTime, setEndTime] = useState("");
	const [decryptionSearchValue, setDecryptionSearchValue] = useState(initialSearch);

	const dispatch = useAppDispatch();
	const translate = useTranslate(); // hook для перевода текста
	const isAccess = useAccessRight();
	const selectedDialogsList = useAppSelector(selectArchiveList);
	const clsHints = useAppSelector(selectClsHints);
	const robotList = useAppSelector(selectRobotList); // список роботов

	const { data: callingList, isLoading: isCallingListLoading, refetch: refetchCallingList } = useGetCallingListQuery({ id: selectedCallListId ?? "" });
	const { data: callingListCash, isLoading: isCallingListCashLoading } = useGetListInstanceQuery({ id: selectedCallListId ?? "" });

	const currentRobot = useMemo(() => robotList.data.find((el) => el.destinations.includes(callingList?.list.source ?? "none")), [callingList?.list.source]);

	// Обновляем callsIdsBySearch при изменении архива
	useEffect(() => {
		if (selectedDialogsList.data) setCallsIdsBySearch(selectedDialogsList.data.map((el) => el.uuid));
	}, [selectedDialogsList]);

	useEffect(() => {
		if (!currentCall && selectedCallRowId !== "none") setSelectedCallRowId("none");
	}, [currentCall]);

	useEffect(() => {
		if (displayMode && displayMode.selectedCallList) {
			setSelectedCallListId(displayMode.selectedCallList ?? null);
			setSelectedRowId("none");
			setSelectedCallRowId("none");
			setSelectedCallsList(undefined);
		}
	}, [displayMode]);

	const normalizeDate = (value: string) => value.replace("T", " ");

	const handleFilterCalls = () => {
		setIsFiltersActive(true);

		if (!startTime) setStartTime(initialStartTime);
		if (!endTime) setEndTime(initialEndTime);
		if (!currentRobot) return;

		const params: IGetArchiveOfDialoguesProps = {
			robotId: currentRobot?.id,
			text: decryptionSearchValue,
			startDate: startTime ? normalizeDate(startTime) : "2025-01-01 00:00",
			endDate: endTime ? normalizeDate(endTime) : getNowTime(),
			hasUnknown: false,
		};

		dispatch(getArchiveOfDialogues(params));
		setSelectedCallRowId("none");
		setSelectedRowId("none");
	};

	const handleResetFilterCalls = () => {
		setIsFiltersActive(false);
		setStartTime("");
		setEndTime("");
		setDecryptionSearchValue("");
	};

	const handleSelectCallsOfDestination = (destinationOfCall: ICurrnetDestiantionTableSelection) => {
		setSelectedRowData(destinationOfCall);
		setSelectedRowId(destinationOfCall.id);
	};

	const handleSelectCall = (call: ICall) => {
		if (selectedCallsList) {
			setSelectedCallRowId(call.uuid);
			onRowClick({ ...call, metadata: selectedCallsList.metadata });
		}
	};

	// Здесь можно добавить tableData, callsTableData, columns, callDetailsColumns и всю логику memo, чтобы хук возвращал готовые данные для Table
	// ...

	return {
		selectedRowId,
		selectedCallRowId,
		selectedCallsList,
		selectedRowData,
		selectedCallListId,
		clsHints,

		currentColumns,

		callsIdsBySearch,
		callingList,
		callingListCash,
		startTime,
		endTime,
		decryptionSearchValue,
		isFiltersActive,

		isCallingListLoading,
		isCallingListCashLoading,

		refetchCallingList,

		handleSelectCallsOfDestination,
		handleSelectCall,
		handleFilterCalls,
		handleResetFilterCalls,

		setStartTime,
		setEndTime,
		setDecryptionSearchValue,
		setCurrentColumns,
		setSelectedCallsList,
		setSelectedRowId,
		setSelectedCallRowId,
		setCallsIdsBySearch,

		dispatch,
		isAccess,
		translate,
	};
};
